import { Link } from "react-router-dom";
import {
    ClipboardCheck,
    Leaf,
    Binoculars,
    Activity,
    Trees,
    Workflow,
    Sprout,
    Database,
    ArrowRight,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Newsletter from "@/components/site/Newsletter";
import { IMAGES, SITE } from "@/lib/site";
import {
    PROGRAM,
    INVESTMENT_AREAS,
    OPEN_QUESTIONS,
    CAREERS,
    TRAINING_PROGRAMS,
    LEARNING_ON_COUNTRY,
    FRAMEWORK,
    LEGACY_OUTCOMES,
} from "@/lib/leadership";

const ICONS = {
    ClipboardCheck,
    Leaf,
    Binoculars,
    Activity,
    Trees,
    Workflow,
    Sprout,
    Database,
};

export default function Leadership() {
    return (
        <main data-testid="leadership-page" className="bg-bone text-ink">
            <Nav />

            {/* HERO */}
            <section
                id="top"
                data-testid="lp-hero"
                className="pt-32 md:pt-40 lg:pt-44 pb-20 md:pb-28 relative"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                        <div className="col-span-12 lg:col-span-8">
                            <div className="flex items-center gap-4 mb-10">
                                <span className="block w-10 h-px bg-ochre" />
                                <span className="eyebrow text-ink2">
                                    {PROGRAM.name} — A {SITE.name} program
                                </span>
                            </div>
                            <h1
                                data-testid="lp-hero-headline"
                                className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[6rem] leading-[0.95] tracking-tight text-ink"
                            >
                                Building Indigenous
                                <br />
                                <em className="italic font-light text-eucalyptus">
                                    leadership
                                </em>{" "}
                                for environmental
                                <br />
                                <span className="text-ochre">stewardship.</span>
                            </h1>
                        </div>

                        <div className="col-span-12 lg:col-span-4 flex flex-col gap-6 lg:pb-4">
                            <div className="rule-top pt-6">
                                <p
                                    data-testid="lp-hero-sub"
                                    className="text-ink2 text-[15px] md:text-base leading-relaxed max-w-md"
                                >
                                    Supporting Indigenous rangers, practitioners
                                    and communities to build careers in{" "}
                                    <span className="text-ink">carbon,</span>{" "}
                                    <span className="text-ink">biodiversity,</span>{" "}
                                    <span className="text-ink">restoration</span>{" "}
                                    and{" "}
                                    <span className="text-ink">environmental management.</span>
                                </p>
                                <p className="mt-4 text-ink2 text-[15px] md:text-base leading-relaxed max-w-md italic">
                                    Country has the answers. Carbon on Country
                                    helps build the skills, pathways and
                                    opportunities needed to care for Country in
                                    a changing world.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                <a
                                    href="#pathways"
                                    data-testid="lp-hero-cta-primary"
                                    className="inline-flex items-center px-6 py-3.5 bg-ink text-bone text-[11px] tracking-[0.22em] uppercase hover:bg-eucalyptus transition-colors"
                                >
                                    Career pathways
                                </a>
                                <Link
                                    to="/#contact"
                                    data-testid="lp-hero-cta-secondary"
                                    className="inline-flex items-center px-6 py-3.5 border border-ink text-ink text-[11px] tracking-[0.22em] uppercase hover:bg-ink hover:text-bone transition-colors"
                                >
                                    Get involved
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY */}
            <section
                data-testid="lp-philosophy"
                className="py-24 md:py-32 lg:py-40 rule-top bg-bone2"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12">
                        <div className="col-span-12 md:col-span-4">
                            <span className="number-mark text-ink2">§ 01 / Philosophy</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink">
                                Country has
                                <br />
                                <em className="italic font-light text-eucalyptus">
                                    the answers.
                                </em>
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-8 md:pl-12">
                            <div className="text-ink2 text-lg md:text-[19px] leading-[1.7] space-y-6 max-w-2xl">
                                <p>
                                    For more than{" "}
                                    <span className="text-ink">60,000 years</span>{" "}
                                    Indigenous peoples have observed, managed
                                    and adapted to environmental change.
                                </p>
                                <p>
                                    Carbon on Country recognises that
                                    environmental stewardship does not begin
                                    with technology.
                                </p>
                                <p className="font-serif italic text-3xl md:text-4xl text-ink leading-[1.2] not-prose">
                                    It begins with Country.
                                </p>
                                <p>
                                    By combining Indigenous knowledge with
                                    modern environmental tools, we can build a
                                    new generation of practitioners equipped to
                                    restore landscapes, monitor change and
                                    strengthen community outcomes.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* OPPORTUNITY + QUESTIONS */}
            <section
                data-testid="lp-opportunity"
                className="py-24 md:py-32 lg:py-40"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12">
                        <div className="col-span-12 md:col-span-5">
                            <span className="number-mark text-ink2">§ 02 / Opportunity</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink">
                                Why Carbon on
                                <br />
                                Country exists.
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-7 md:pl-12">
                            <p className="text-ink2 text-lg md:text-xl leading-relaxed max-w-2xl">
                                Australia is investing billions into:
                            </p>
                            <ul
                                data-testid="lp-investment-list"
                                className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3"
                            >
                                {INVESTMENT_AREAS.map((a, i) => (
                                    <li
                                        key={a}
                                        className="flex items-center gap-3 text-ink font-serif text-xl md:text-2xl"
                                    >
                                        <span className="number-mark text-ochre">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="mt-16 md:mt-24 border-y border-rule py-14 md:py-20">
                        <p className="font-serif italic text-2xl md:text-3xl text-ink2 max-w-3xl">
                            Yet a critical question remains.
                        </p>
                        <ul
                            data-testid="lp-questions"
                            className="mt-10 space-y-4 md:space-y-5"
                        >
                            {OPEN_QUESTIONS.map((q, i) => (
                                <li
                                    key={q}
                                    className="flex items-start gap-6 group"
                                >
                                    <span className="number-mark text-ochre pt-3 shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.15] text-ink">
                                        {q}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-12 text-ink2 text-lg md:text-xl leading-relaxed max-w-2xl">
                            Carbon on Country exists to help answer these
                            questions —{" "}
                            <span className="text-ink">
                                with Indigenous practitioners leading the work.
                            </span>
                        </p>
                    </div>
                </div>
            </section>

            {/* LEADERSHIP GAP */}
            <section
                data-testid="lp-gap"
                className="py-24 md:py-32 lg:py-40 bg-ink text-bone relative overflow-hidden grain"
            >
                <img
                    src={IMAGES.eucalyptus}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/95 via-ink/85 to-ink/95" />

                <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12">
                        <div className="col-span-12 md:col-span-5">
                            <span className="number-mark text-ochre">§ 03 / The shift</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-bone">
                                From participation
                                <br />
                                to{" "}
                                <em className="italic font-light text-bone/80">
                                    leadership.
                                </em>
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-7 md:pl-12">
                            <div className="text-bone/75 text-lg md:text-[19px] leading-[1.7] space-y-6 max-w-2xl">
                                <p>
                                    For decades Indigenous people have been
                                    consulted about environmental projects.
                                </p>
                                <p className="font-serif italic text-3xl md:text-4xl text-bone leading-[1.15]">
                                    The next step is leadership.
                                </p>
                                <p>
                                    Carbon on Country supports pathways into
                                    roles where Indigenous practitioners are not
                                    simply participating in projects but
                                    leading, managing, assessing and monitoring
                                    them.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CAREERS / PATHWAYS */}
            <section
                id="pathways"
                data-testid="lp-pathways"
                className="py-24 md:py-32 lg:py-40"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12 mb-14 md:mb-20">
                        <div className="col-span-12 md:col-span-6">
                            <span className="number-mark text-ink2">§ 04 / Pathways</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink">
                                Emerging
                                <br />
                                environmental
                                <br />
                                <em className="italic font-light text-eucalyptus">
                                    careers.
                                </em>
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-6 md:pl-12 flex items-end">
                            <p className="text-ink2 text-lg md:text-xl leading-relaxed max-w-xl">
                                Eight career pathways — each one is real work,
                                already in demand, and a place where Indigenous
                                practitioners can lead the next generation of
                                environmental stewardship.
                            </p>
                        </div>
                    </div>

                    <div
                        data-testid="lp-careers-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-rule"
                    >
                        {CAREERS.map((c, i) => {
                            const Icon = ICONS[c.icon];
                            return (
                                <article
                                    key={c.title}
                                    data-testid={`lp-career-${i}`}
                                    className="border-r border-b border-rule p-8 md:p-9 bg-bone hover:bg-bone2 transition-colors group"
                                >
                                    <div className="flex items-start justify-between mb-7">
                                        {Icon && (
                                            <Icon
                                                size={28}
                                                strokeWidth={1.4}
                                                className="text-eucalyptus group-hover:text-ochre transition-colors"
                                            />
                                        )}
                                        <span className="number-mark text-ochre">
                                            — {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <h3 className="font-serif text-2xl md:text-[26px] leading-[1.15] text-ink">
                                        {c.title}
                                    </h3>
                                    <p className="mt-4 text-ink2 text-[15px] leading-relaxed">
                                        {c.body}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FUTURE GENERATIONS */}
            <section
                data-testid="lp-future"
                className="py-24 md:py-32 lg:py-40 bg-bone2"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
                        <div className="col-span-12 lg:col-span-5">
                            <span className="number-mark text-ink2">§ 05 / Generations</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink">
                                Investing
                                <br />
                                in future
                                <br />
                                <em className="italic font-light text-eucalyptus">
                                    generations.
                                </em>
                            </h2>
                        </div>
                        <div className="col-span-12 lg:col-span-7 lg:pl-12 lg:pt-10">
                            <div className="text-ink2 text-lg md:text-[19px] leading-[1.7] space-y-6 max-w-2xl">
                                <p>
                                    The future environmental workforce will be
                                    built by today&rsquo;s rangers, young leaders and
                                    community practitioners.
                                </p>
                                <p>
                                    Carbon on Country supports mentoring,
                                    training and career pathways that strengthen
                                    Indigenous leadership across the
                                    environmental sector.
                                </p>
                                <p className="font-serif italic text-3xl md:text-4xl text-ink leading-[1.15] pt-2">
                                    Environmental stewardship is not a
                                    short-term project. It is an
                                    intergenerational responsibility.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRAINING PROGRAMS + LEARNING ON COUNTRY */}
            <section
                data-testid="lp-training"
                className="py-24 md:py-32 lg:py-40"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12">
                        <div className="col-span-12 md:col-span-5">
                            <span className="number-mark text-ink2">§ 06 / Training</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink">
                                Training
                                <br />
                                programs.
                            </h2>
                            <p className="mt-6 text-ink2 text-[15px] leading-relaxed max-w-sm">
                                Nine modular programs — designed for delivery
                                on Country and adapted for the skills, language
                                and pace of the people doing the work.
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-7 md:pl-12">
                            <ul
                                data-testid="lp-training-list"
                                className="divide-y divide-rule border-t border-b border-rule"
                            >
                                {TRAINING_PROGRAMS.map((t, i) => (
                                    <li
                                        key={t}
                                        data-testid={`lp-training-${i}`}
                                        className="py-5 md:py-6 flex items-baseline justify-between gap-6 group hover:text-ochre transition-colors"
                                    >
                                        <span className="font-serif text-xl md:text-2xl lg:text-[26px] leading-[1.15] text-ink group-hover:text-ochre transition-colors">
                                            {t}
                                        </span>
                                        <span className="number-mark text-ink2 shrink-0">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="mt-24 md:mt-32 grid grid-cols-12 gap-6 md:gap-12">
                        <div className="col-span-12 md:col-span-5">
                            <span className="number-mark text-ink2">§ 07 / On Country</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink">
                                Learning
                                <br />
                                <em className="italic font-light text-eucalyptus">
                                    through practice.
                                </em>
                            </h2>
                            <p className="mt-6 text-ink2 text-lg leading-relaxed max-w-md">
                                Real capability is built on Country. We
                                prioritise practical learning and on-Country
                                delivery wherever possible.
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-7 md:pl-12">
                            <div
                                data-testid="lp-onpractice-list"
                                className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 border-t border-rule pt-8"
                            >
                                {LEARNING_ON_COUNTRY.map((item, i) => (
                                    <div
                                        key={item}
                                        className="flex items-start gap-4"
                                    >
                                        <span className="number-mark text-ochre pt-2">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-serif text-xl md:text-2xl leading-[1.2] text-ink">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FRAMEWORK */}
            <section
                data-testid="lp-framework"
                className="py-24 md:py-32 lg:py-40 bg-bone2"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12">
                        <div className="col-span-12 md:col-span-7">
                            <span className="number-mark text-ink2">§ 08 / Framework</span>
                            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink max-w-3xl">
                                The environmental
                                stewardship
                                framework.
                            </h2>
                        </div>
                        <div className="col-span-12 md:col-span-5 md:pl-6 flex items-end">
                            <p className="text-ink2 text-lg leading-relaxed max-w-md">
                                Five interconnected keystones. Together they
                                form the operating model for Indigenous
                                Environmental Leadership.
                            </p>
                        </div>
                    </div>

                    <ol
                        data-testid="lp-framework-list"
                        className="divide-y divide-rule border-t border-b border-rule"
                    >
                        {FRAMEWORK.map((f, i) => (
                            <li
                                key={f.n}
                                data-testid={`lp-framework-${i}`}
                                className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 group hover:bg-bone transition-colors px-2 md:px-4"
                            >
                                <div className="col-span-2 md:col-span-1">
                                    <span className="font-serif text-2xl md:text-3xl italic text-ochre">
                                        {f.n}
                                    </span>
                                </div>
                                <div className="col-span-10 md:col-span-5">
                                    <h3 className="font-serif text-2xl md:text-3xl lg:text-[34px] leading-[1.05] text-ink">
                                        {f.title}
                                    </h3>
                                </div>
                                <div className="col-span-12 md:col-span-6 md:pl-6">
                                    <p className="text-ink2 text-[16px] md:text-lg leading-relaxed max-w-xl">
                                        {f.body}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* WHY IT MATTERS */}
            <section
                data-testid="lp-matters"
                className="py-24 md:py-32 lg:py-40 bg-ink text-bone relative overflow-hidden grain"
            >
                <img
                    src={IMAGES.earth}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/95 via-ink/85 to-ink/95" />

                <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="grid grid-cols-12 gap-6 md:gap-12">
                        <div className="col-span-12 md:col-span-4">
                            <span className="number-mark text-ochre">§ 09 / Why it matters</span>
                        </div>
                        <div className="col-span-12 md:col-span-8">
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-bone max-w-3xl">
                                Strong Country
                                requires{" "}
                                <em className="italic font-light text-bone/80">
                                    strong people.
                                </em>
                            </h2>

                            <div className="mt-10 space-y-6 text-bone/75 text-lg md:text-[19px] leading-[1.7] max-w-2xl">
                                <p>
                                    Environmental investment should do more than
                                    generate reports, credits and projects.
                                </p>
                                <p>It should leave behind:</p>
                            </div>

                            <ul
                                data-testid="lp-legacy"
                                className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl"
                            >
                                {LEGACY_OUTCOMES.map((o, i) => (
                                    <li
                                        key={o}
                                        className="flex items-baseline gap-4 font-serif text-2xl md:text-3xl text-bone"
                                    >
                                        <span className="number-mark text-ochre">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {o}
                                    </li>
                                ))}
                            </ul>

                            <p className="mt-14 font-serif italic text-3xl md:text-4xl lg:text-5xl text-bone leading-[1.15] max-w-3xl">
                                The greatest environmental asset Australia
                                possesses is not carbon. It is people who care
                                for Country.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CALL TO ACTION */}
            <section
                data-testid="lp-cta"
                className="py-24 md:py-32 lg:py-40"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="border-y border-rule py-16 md:py-24">
                        <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
                            <div className="col-span-12 md:col-span-7">
                                <span className="number-mark text-ink2">§ 10 / Join</span>
                                <h2
                                    data-testid="lp-cta-heading"
                                    className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink max-w-3xl"
                                >
                                    Join the next generation of
                                    <br />
                                    <em className="italic font-light text-eucalyptus">
                                        environmental leaders.
                                    </em>
                                </h2>
                                <p className="mt-8 text-ink2 text-lg md:text-xl leading-relaxed max-w-2xl">
                                    Whether you are an Indigenous ranger,
                                    Traditional Owner organisation, community
                                    group or environmental practitioner —
                                    Carbon on Country can help build the skills
                                    and pathways needed to steward Country for
                                    future generations.
                                </p>
                                <p className="mt-6 font-serif italic text-2xl md:text-3xl text-ink leading-[1.2] max-w-2xl">
                                    Country has the answers. Together we can
                                    build the capability to listen.
                                </p>
                            </div>
                            <div className="col-span-12 md:col-span-5 md:pl-8 flex flex-col gap-4 md:pt-16">
                                <Link
                                    to="/#contact"
                                    data-testid="lp-cta-primary"
                                    className="inline-flex items-center justify-between px-7 py-5 bg-ink text-bone text-[12px] tracking-[0.22em] uppercase hover:bg-eucalyptus transition-colors"
                                >
                                    Start a conversation
                                    <ArrowRight size={16} strokeWidth={1.5} />
                                </Link>
                                <a
                                    href={`mailto:${SITE.email}?subject=Carbon%20on%20Country%20-%20Leadership%20pathways`}
                                    data-testid="lp-cta-mailto"
                                    className="inline-flex items-center justify-between px-7 py-5 border border-ink text-ink text-[12px] tracking-[0.22em] uppercase hover:bg-ink hover:text-bone transition-colors"
                                >
                                    Email the team
                                    <ArrowRight size={16} strokeWidth={1.5} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Newsletter />
            <Footer />
        </main>
    );
}
